import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  AdmissionSettings,
  AdmissionSettingsPayload,
  AdmissionStatus,
  ApplicationDetail,
  ApplicationSummary,
  EnrolResult,
  PublicAdmissions,
  PublicApplicationStatus,
  SectionOption,
  SubmitResult,
  UploadedDocument,
} from './types'

export const admissionKeys = {
  all: ['admissions'] as const,
  public: () => ['admissions-public'] as const,
  settings: () => [...admissionKeys.all, 'settings'] as const,
  list: (params: object) => [...admissionKeys.all, 'list', params] as const,
  counts: (params: object) => [...admissionKeys.all, 'counts', params] as const,
  one: (id: number) => [...admissionKeys.all, 'one', id] as const,
  sections: (id: number) => [...admissionKeys.all, 'sections', id] as const,
}

/* Public form (no sign-in; the API answers for the school of this address). */

export const publicAdmissionsQuery = () =>
  queryOptions({
    queryKey: admissionKeys.public(),
    queryFn: () => api.get<PublicAdmissions>('/public/admissions'),
    retry: false,
  })

export function uploadAdmissionDocument(file: File, onProgress?: (fraction: number) => void) {
  const body = new FormData()
  body.append('file', file)
  return api.post<UploadedDocument>('/public/admissions/files', body, {
    onUploadProgress: (e) => {
      if (e.total) onProgress?.(e.loaded / e.total)
    },
  })
}

export interface SubmitPayload {
  classId: number
  student: Record<string, string | null>
  guardians: {
    relation: string
    name: string
    phone: string | null
    email: string | null
    occupation: string | null
    isPrimary: boolean
  }[]
  documents: { index: number; uploadToken: string }[]
  consent: boolean
  website: string
}

export const submitApplication = (payload: SubmitPayload) =>
  api.post<SubmitResult>('/public/admissions', payload)

export const fetchApplicationStatus = (reference: string, email: string) =>
  api.get<PublicApplicationStatus>(`/public/admissions/${encodeURIComponent(reference.trim())}`, {
    params: { email: email.trim() },
  })

/* School office. */

export interface ApplicationListParams {
  status?: string
  classId?: string
  search?: string
  page?: number
  perPage?: number
}

export const admissionSettingsQuery = () =>
  queryOptions({
    queryKey: admissionKeys.settings(),
    queryFn: () => api.get<AdmissionSettings>('/admissions/settings'),
  })
export const applicationsQuery = (params: ApplicationListParams) =>
  queryOptions({
    queryKey: admissionKeys.list(params),
    queryFn: () => api.page<ApplicationSummary>('/admissions/applications', params),
    placeholderData: (prev) => prev,
  })
export const applicationCountsQuery = (params: { classId?: string }) =>
  queryOptions({
    queryKey: admissionKeys.counts(params),
    queryFn: () =>
      api.get<Record<AdmissionStatus, number>>('/admissions/applications/counts', {
        params: params.classId ? { classId: params.classId } : undefined,
      }),
  })
export const applicationQuery = (id: number) =>
  queryOptions({
    queryKey: admissionKeys.one(id),
    queryFn: () => api.get<ApplicationDetail>(`/admissions/applications/${id}`),
  })
export const applicationSectionsQuery = (id: number) =>
  queryOptions({
    queryKey: admissionKeys.sections(id),
    queryFn: () => api.get<SectionOption[]>(`/admissions/applications/${id}/sections`),
  })

function useAdmissionMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: admissionKeys.all })
    },
  })
}

export const useSaveAdmissionSettings = () =>
  useAdmissionMutation((body: AdmissionSettingsPayload) =>
    api.put<AdmissionSettings>('/admissions/settings', body),
  )
export const useChangeApplicationStatus = (id: number) =>
  useAdmissionMutation((body: { status: AdmissionStatus; reason?: string | null }) =>
    api.post<ApplicationDetail>(`/admissions/applications/${id}/status`, body),
  )
export const useAddApplicationNote = (id: number) =>
  useAdmissionMutation((body: { body: string }) =>
    api.post<ApplicationDetail>(`/admissions/applications/${id}/notes`, body),
  )
export function useEnrolApplication(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      sectionId: number
      rollNumber?: string | null
      createPortalAccount: boolean
      notifyFamily: boolean
    }) => api.post<EnrolResult>(`/admissions/applications/${id}/enrol`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: admissionKeys.all })
      void qc.invalidateQueries({ queryKey: ['students'] })
    },
  })
}
